import { Component } from '@theme/component';
import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * Pixels subtracted from the measured header bottom when setting `--kith-header-offset`
 * (used as drawer `top`). Keep this at 0 so the drawer aligns exactly to the
 * visible bottom edge of the Kith header.
 */
const KITH_DRAWER_TOP_TRIM_PX = 0;

/** Scroll movement needed before a direction counts; smaller deltas accumulate until they do. */
const KITH_SCROLL_DELTA_PX = 2;

/**
 * Custom header component for Kith menu drawer, sticky on scroll up only.
 * The header never animates out: it scrolls away with the page like normal content.
 * - `inactive`: at the top of the page in its natural spot (quick links showing).
 * - `parked`: a revealed header left in the page at `--kith-header-anchor` once the
 *   shopper scrolls down again, so it scrolls away at exactly the page's speed.
 * - `idle`: fully scrolled away and held just above the viewport.
 * - `active`: slid back in on scroll up and pinned to the top.
 */
class KithHeaderComponent extends Component {
  /**
   * Whether the header has been scrolled offscreen on mobile
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for the reveal animation
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for the reveal animation
   * @constant {number}
   */
  #animationDelay = 280;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /** @type {number | null} */
  #metricsRaf = null;

  /** @type {number | null} */
  #scrollRaf = null;

  /** @type {number | null} */
  #initialStateRaf = null;

  /** @type {number | null} */
  #lastOffsetPx = null;

  /** @type {number | null} */
  #lastHeightPx = null;

  /** @type {HTMLElement | null} */
  #headerGroup = null;

  /** @type {number} Scroll offset the header is parked at while `parked` (0 while `inactive`). */
  #anchor = 0;

  /** @type {HTMLElement | null} Quick links bar, which hangs below the group while `inactive`. */
  #quickLinks = null;

  /** @type {number} Cached border-box height of the quick links bar. */
  #quickLinksHeight = 0;

  /** @type {number | null} */
  #lastAnchorPx = null;

  /** @type {number | null} Cached border-box height of the header (kept fresh by ResizeObserver, no layout read). */
  #selfHeight = null;

  /** @type {number | null} Cached border-box height of the header group. */
  #groupHeight = null;

  /** @type {boolean} Whether the ResizeObserver is already watching the header group. */
  #headerGroupObserved = false;

  /**
   * The visual sticky surface is the whole Shopify header group: announcement
   * bar + Kith header. Mirror the component state there so CSS can move the
   * complete stack instead of only hiding the inner navigation row.
   */
  #syncHeaderGroupState = () => {
    const headerGroup = this.#headerGroup || this.closest('#header-group') || document.getElementById('header-group');
    if (!(headerGroup instanceof HTMLElement)) return;

    this.#headerGroup = headerGroup;
    if (!this.#headerGroupObserved) {
      this.#resizeObserver.observe(headerGroup);
      this.#headerGroupObserved = true;
      const quickLinks = headerGroup.querySelector('.topshelf-quick-links');
      if (quickLinks instanceof HTMLElement) {
        this.#quickLinks = quickLinks;
        this.#resizeObserver.observe(quickLinks);
      }
    }
    const stickyState = this.dataset.stickyState || 'inactive';
    const scrollDirection = this.dataset.scrollDirection || 'none';
    const isAnimating = this.hasAttribute('data-animating');

    if (headerGroup.dataset.kithStickyState !== stickyState) {
      headerGroup.dataset.kithStickyState = stickyState;
    }
    if (headerGroup.dataset.kithScrollDirection !== scrollDirection) {
      headerGroup.dataset.kithScrollDirection = scrollDirection;
    }
    if (this.#lastAnchorPx !== this.#anchor) {
      this.#lastAnchorPx = this.#anchor;
      headerGroup.style.setProperty('--kith-header-anchor', `${this.#anchor}px`);
    }
    if (isAnimating && !headerGroup.hasAttribute('data-kith-animating')) {
      headerGroup.setAttribute('data-kith-animating', '');
    } else if (!isAnimating && headerGroup.hasAttribute('data-kith-animating')) {
      headerGroup.removeAttribute('data-kith-animating');
    }
  };

  #setAnimating = (isAnimating) => {
    if (this.#timeout != null) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (!isAnimating) {
      this.removeAttribute('data-animating');
      this.#syncHeaderGroupState();
      return;
    }

    this.setAttribute('data-animating', '');
    this.#syncHeaderGroupState();
    this.#timeout = window.setTimeout(() => {
      this.#timeout = null;
      this.removeAttribute('data-animating');
      this.#syncHeaderGroupState();
    }, this.#animationDelay);
  };

  /**
   * Drawer `top` must match the header bar's bottom edge in the viewport.
   * Using height alone skips y-offset (e.g. content above the bar) and integer rounding can leave a 1px gap.
   */
  #updateHeaderMetrics = () => {
    // ResizeObserver supplies both measurements without a synchronous layout
    // read. Wait for its initial delivery instead of falling back to
    // getBoundingClientRect during custom-element connection.
    if (this.#selfHeight == null || this.#groupHeight == null) return;

    const heightPx = Math.round(this.#selfHeight);
    const offsetPx = Math.max(0, this.#groupHeight - KITH_DRAWER_TOP_TRIM_PX);

    if (offsetPx === this.#lastOffsetPx && heightPx === this.#lastHeightPx) return;

    this.#lastOffsetPx = offsetPx;
    this.#lastHeightPx = heightPx;
    document.body.style.setProperty('--kith-header-offset', `${offsetPx}px`);
    document.body.style.setProperty('--kith-drawer-top', `${offsetPx}px`);
    document.body.style.setProperty('--header-height', `${heightPx}px`);
    document.body.style.setProperty('--header-group-height', `${heightPx}px`);
  };

  #scheduleHeaderMetrics = () => {
    if (this.#metricsRaf != null) return;
    this.#metricsRaf = requestAnimationFrame(() => {
      this.#metricsRaf = null;
      this.#updateHeaderMetrics();
    });
  };

  /**
   * Keeps `--header-height` and `--kith-header-offset` aligned with the live layout
   */
  #resizeObserver = new ResizeObserver((entries) => {
    // Read sizes straight from the observer entries (free — no forced reflow) and cache
    // them so scroll-driven sticky transitions never need getBoundingClientRect.
    for (const entry of entries) {
      const box = entry.borderBoxSize && entry.borderBoxSize[0];
      const height = box ? box.blockSize : entry.contentRect.height;
      if (entry.target === this) {
        this.#selfHeight = height;
      } else if (entry.target === this.#headerGroup) {
        this.#groupHeight = height;
      } else if (entry.target === this.#quickLinks) {
        this.#quickLinksHeight = height;
      }
    }
    this.#scheduleHeaderMetrics();
  });

  /**
   * Observes the header while scrolling the viewport to track when it's actively sticky
   */
  #observeStickyPosition = () => {
    if (this.#intersectionObserver) return;

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;
      this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
    }, { threshold: 0, root: getIntersectionRoot() });

    this.#intersectionObserver.observe(this);
  };

  #handleScroll = () => {
    if (this.#scrollRaf != null) return;
    this.#scrollRaf = requestAnimationFrame(() => {
      this.#scrollRaf = null;
      this.#processScroll();
    });
  };

  /**
   * @param {'inactive' | 'parked' | 'active' | 'idle'} stickyState
   * @param {{ anchor?: number, scrollDirection?: string, animate?: boolean }} [options]
   */
  #setStickyState = (stickyState, { anchor = this.#anchor, scrollDirection = this.dataset.scrollDirection, animate = false } = {}) => {
    if (
      this.dataset.stickyState === stickyState &&
      this.#anchor === anchor &&
      this.dataset.scrollDirection === scrollDirection
    ) {
      return;
    }

    this.#anchor = anchor;
    this.dataset.stickyState = stickyState;
    this.dataset.scrollDirection = scrollDirection || 'none';
    if (animate) {
      this.#setAnimating(true);
    } else if (stickyState === 'idle') {
      // The header is already off screen; cut any running reveal so it can't slide back out.
      this.#setAnimating(false);
    } else {
      this.#syncHeaderGroupState();
    }
    this.#scheduleHeaderMetrics();
  };

  #processScroll = () => {
    const scrollTop = Math.max(0, getScrollTop());
    const delta = scrollTop - this.#lastScrollTop;
    const isScrollingDown = delta >= KITH_SCROLL_DELTA_PX;
    const isScrollingUp = delta <= -KITH_SCROLL_DELTA_PX;

    if (isScrollingDown || isScrollingUp) {
      this.#lastScrollTop = scrollTop;
    }

    // Open drawers are fixed to the header's bottom edge, so it must stay put under them.
    if (this.querySelector('.kith-menu-drawer.is-open')) return;

    const stickyState = this.dataset.stickyState || 'inactive';

    if (scrollTop === 0) {
      this.#setStickyState('inactive', { anchor: 0, scrollDirection: 'none' });
    } else if (stickyState === 'inactive') {
      if (this.#groupHeight && scrollTop >= this.#groupHeight + this.#quickLinksHeight) {
        this.#setStickyState('idle', { scrollDirection: 'down' });
      }
    } else if (stickyState === 'parked') {
      if (scrollTop < this.#anchor) {
        // Scrolling up past the parked spot would open a gap above it; pin it to the top instead.
        this.#setStickyState('active', { scrollDirection: 'up' });
      } else if (this.#groupHeight && scrollTop >= this.#anchor + this.#groupHeight) {
        this.#setStickyState('idle', { scrollDirection: 'down' });
      }
    } else if (stickyState === 'active') {
      if (isScrollingDown) {
        this.#setStickyState('parked', { anchor: scrollTop, scrollDirection: 'down' });
      }
    } else if (isScrollingUp) {
      this.#setStickyState('active', { scrollDirection: 'up', animate: true });
    }
  };

  /**
   * A header partly scrolled away still opens drawers and popovers sized for a fully visible
   * one, so pin it first. This runs on click (capture, ahead of the target's handlers) and
   * keyboard focus only: pinning on press would shift the header under the pointer before
   * release and swallow the click.
   * @param {Event} event
   */
  #handleInteraction = (event) => {
    if (event.type === 'focusin' && !(event.target instanceof Element && event.target.matches(':focus-visible'))) return;

    const stickyState = this.dataset.stickyState;
    if (stickyState !== 'inactive' && stickyState !== 'parked') return;
    if (getScrollTop() <= this.#anchor) return;

    this.#setStickyState('active');
  };

  /** @param {number} scrollTop */
  #parkAt = (scrollTop) => {
    this.#setStickyState(scrollTop > 0 ? 'parked' : 'inactive', { anchor: scrollTop, scrollDirection: 'none' });
  };

  #handleViewportChange = () => {
    // Viewport changes don't change the scroll-up-only behavior anymore – just
    // refresh measurements. State is driven entirely by scroll position.
    this.#scheduleHeaderMetrics();
  };

  /**
   * At 990px and up Horizon scrolls `.page-wrapper` rather than the document, so both
   * the scroll event target and the observer root have to be rebound on a breakpoint change.
   */
  #handleBreakpointChange = () => {
    this.#scrollContainer?.removeEventListener('scroll', this.#handleScroll);
    this.#scrollContainer = getScrollEventTarget();
    this.#scrollContainer.addEventListener('scroll', this.#handleScroll, { passive: true });

    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#observeStickyPosition();

    // The scroll container changed, so re-park at the new container's offset.
    this.#lastScrollTop = Math.max(0, getScrollTop());
    this.#parkAt(this.#lastScrollTop);
  };

  connectedCallback() {
    super.connectedCallback();
    document.body.classList.add('kith-header-mounted');
    this.#resizeObserver.observe(this);
    this.#observeStickyPosition();
    this.#scheduleHeaderMetrics();
    this.#scrollContainer = getScrollEventTarget();
    this.#scrollContainer.addEventListener('scroll', this.#handleScroll, { passive: true });
    window.addEventListener('resize', this.#handleViewportChange, { passive: true });
    scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    this.addEventListener('click', this.#handleInteraction, { capture: true });
    this.addEventListener('focusin', this.#handleInteraction);

    this.#syncHeaderGroupState();

    // Reading scrollTop while the custom element is connecting forces the
    // browser to lay out the partially constructed page. Wait until the first
    // animation frame, when that initial layout is already available.
    this.#initialStateRaf = requestAnimationFrame(() => {
      this.#initialStateRaf = null;
      if (!this.isConnected) return;
      this.#lastScrollTop = Math.max(0, getScrollTop());
      this.#parkAt(this.#lastScrollTop);
    });
  }



  disconnectedCallback() {
    super.disconnectedCallback();
    document.body.classList.remove('kith-header-mounted');
    this.#resizeObserver.disconnect();
    this.#headerGroupObserved = false;
    this.#selfHeight = null;
    this.#groupHeight = null;
    this.#intersectionObserver?.disconnect();
    if (this.#metricsRaf != null) {
      cancelAnimationFrame(this.#metricsRaf);
      this.#metricsRaf = null;
    }
    if (this.#scrollRaf != null) {
      cancelAnimationFrame(this.#scrollRaf);
      this.#scrollRaf = null;
    }
    if (this.#initialStateRaf != null) {
      cancelAnimationFrame(this.#initialStateRaf);
      this.#initialStateRaf = null;
    }
    if (this.#timeout != null) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }
    this.#scrollContainer?.removeEventListener('scroll', this.#handleScroll);
    this.#scrollContainer = null;
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    window.removeEventListener('resize', this.#handleViewportChange);
    this.removeEventListener('click', this.#handleInteraction, { capture: true });
    this.removeEventListener('focusin', this.#handleInteraction);
    document.body.style.setProperty('--header-height', '0px');
    document.body.style.setProperty('--header-group-height', '0px');
    document.body.style.setProperty('--kith-header-offset', '0px');
    document.body.style.removeProperty('--kith-drawer-top');
    this.#headerGroup?.removeAttribute('data-kith-sticky-state');
    this.#headerGroup?.removeAttribute('data-kith-scroll-direction');
    this.#headerGroup?.removeAttribute('data-kith-animating');
    this.#headerGroup?.style.removeProperty('--kith-header-anchor');
    this.#headerGroup = null;
    this.#lastAnchorPx = null;
    this.#quickLinks = null;
    this.#quickLinksHeight = 0;
  }
}

customElements.define('kith-header-component', KithHeaderComponent); 